import React, { useState, useEffect, useMemo, memo, useRef } from "react";
import useSWR, { mutate as globalMutate } from "swr";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import { formatDateToReadable } from "../../../utilities/dateTime";
import SkeletonTable from "../../InventoryManagement/components/Common/SkeletonTable";
import ErrorCard from "../../InventoryManagement/components/Common/ErrorCard";
import EmptyCard from "../../InventoryManagement/components/Common/EmptyCard";
import LoadMorePagination, {
  ResultsCounter,
} from "../../InventoryManagement/components/Common/LoadMorePagination";
import { buildGetUsersUrl, userFetcher } from "../services/userService";
import { getTaggingText } from "../utils/userHelper";
import UserProfileIcon from "../../../img/user_account.svg";
import UserProfileSelectedIcon from "../../../img/user_account_selected.svg";
import TrashIcon from "../../../img/trash.svg";
import TrashSelectedIcon from "../../../img/trash_selected.svg";
import RefreshIcon from "../../../img/refresh.svg";
import RefreshSelectedIcon from "../../../img/refresh_selected.svg";
import EditPenIcon from "../../../img/edit_pen.svg";
import EditPenSelectedIcon from "../../../img/edit_pen_selected.svg";
import PopperMenu from "../../Shared/PopperMenu/PopperMenu";

const pageSize = 10;

const getUserStatusBadgeClass = (isActive) => {
  return isActive
    ? "user-status-badge active"
    : "user-status-badge deactivated";
};
const getUserStatusText = (isActive) => {
  return isActive ? "Active" : "Deactivated";
};

const extractUsersFromApiData = (apiData) => {
  return apiData?.data || apiData?.users || apiData?.results || [];
};

const UserRow = memo(
  ({
    user,
    index,
    activeActionMenuId,
    activeActionMenuAnchor,
    onActionMenuToggle,
    onCloseActionMenu,
    onEditUser,
    onStatusActionClick,
  }) => {
    const actionMenuOption = [
      {
        id: "profile",
        icon: UserProfileIcon,
        selectedIcon: UserProfileSelectedIcon,
        text: "View Profile",
        to: `/user-profile?userId=${user.id}`,
        onClick: onCloseActionMenu,
        alt: "Profile Icon",
      },
      {
        id: "edit",
        icon: EditPenIcon,
        selectedIcon: EditPenSelectedIcon,
        text: "Edit User",
        onClick: () => {
          if (onEditUser) {
            onEditUser(user.id);
          }
          onCloseActionMenu();
        },
        alt: "Edit Icon",
        isHidden: !user.is_active,
      },
      {
        id: "deactivate",
        icon: user.is_active ? TrashIcon : RefreshIcon,
        selectedIcon: user.is_active ? TrashSelectedIcon : RefreshSelectedIcon,
        text: user.is_active ? "Deactivate" : "Activate",
        onClick: () => {
          if (onStatusActionClick) {
            onStatusActionClick(user);
          }
          onCloseActionMenu();
        },
        alt: "Deactivate Icon",
      },
    ];

    return (
      <tr key={user.id || index} className="table-row">
        <td>{index + 1}.</td>
        <td>{user.name || "-"}</td>
        <td>{user.id_number || "-"}</td>
        <td>{user.phone_number || "-"}</td>
        <td title={user.tagging}>{user.tagging}</td>
        <td>{user.joining_date}</td>
        <td>
          <div className={getUserStatusBadgeClass(user.is_active)}>
            <span className="user-status-badge-text">
              {getUserStatusText(user.is_active)}
            </span>
          </div>
        </td>
        <td>
          <div className="action-three-dot">
            {activeActionMenuId === user.id && (
              <PopperMenu
                className="action-menu-container"
                anchorElement={activeActionMenuAnchor}
                onRequestClose={onCloseActionMenu}
                menuItems={actionMenuOption}
              />
            )}
            <MoreVertIcon
              style={{
                cursor: "pointer",
                color: "#362B737A",
                height: "24px",
                width: "24px",
              }}
              onClick={(event) => onActionMenuToggle(event, user.id)}
            />
          </div>
        </td>
      </tr>
    );
  }
);

UserRow.displayName = "UserRow";
const UserListTable = ({
  searchTerm,
  status = "",
  userType = "",
  refreshTrigger,
  onEditUser,
  onStatusActionClick,
}) => {
  const [page, setPage] = useState(0);
  const [activeActionMenuId, setActiveActionMenuId] = useState(null);
  const [activeActionMenuAnchor, setActiveActionMenuAnchor] = useState(null);
  const [accumulatedUsers, setAccumulatedUsers] = useState([]);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [previousPage, setPreviousPage] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [currentFilters, setCurrentFilters] = useState({
    searchTerm: "",
    status: "",
    userType: "",
  });
  const refreshCount = refreshTrigger?.count || 0;
  const editedUserId = refreshTrigger?.editedUserId || null;

  const accumulatedUsersRef = useRef(accumulatedUsers);
  useEffect(() => {
    accumulatedUsersRef.current = accumulatedUsers;
  });
  const currentFiltersRef = useRef(currentFilters);
  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  });

  useEffect(
    function resetOnFilterChange() {
      const filtersChanged =
        currentFilters.searchTerm !== searchTerm ||
        currentFilters.status !== status ||
        currentFilters.userType !== userType;

      if (filtersChanged) {
        setPage(0);
        setAccumulatedUsers([]);
        setLoadMoreLoading(false);
        setPreviousPage(0);
        setScrollPosition(0);
        setCurrentFilters({
          searchTerm,
          status,
          userType,
        });
      }
    },
    [searchTerm, status, userType, currentFilters]
  );

  useEffect(() => {
    if (page > previousPage) {
      setScrollPosition(
        window.pageYOffset || document.documentElement.scrollTop
      );
      setLoadMoreLoading(true);
    }
    setPreviousPage(page);
  }, [page, previousPage]);

  const handleActionMenuToggle = (event, userId) => {
    if (activeActionMenuId === userId) {
      setActiveActionMenuId(null);
      setActiveActionMenuAnchor(null);
      return;
    }

    setActiveActionMenuAnchor(event.currentTarget);
    setActiveActionMenuId(userId);
  };

  const closeActionMenu = () => {
    setActiveActionMenuId(null);
    setActiveActionMenuAnchor(null);
  };

  const apiUrl = useMemo(
    () =>
      buildGetUsersUrl({
        pageNo: page,
        pageSize,
        userType,
        query: searchTerm,
        status,
      }),
    [page, searchTerm, status, userType]
  );

  const {
    data: apiData,
    error,
    mutate,
  } = useSWR(apiUrl, userFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    errorRetryCount: 3,
    errorRetryInterval: 1000,
    refreshInterval: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!refreshCount) {
      return;
    }

    const refreshUserList = async () => {
      setRefreshLoading(true);
      if (editedUserId) {
        const users = accumulatedUsersRef.current;
        const userIndex = users.findIndex((u) => u.id === editedUserId);

        if (userIndex !== -1) {
          const targetPage = Math.floor(userIndex / pageSize);
          const filters = currentFiltersRef.current;
          const targetUrl = buildGetUsersUrl({
            pageNo: targetPage,
            pageSize,
            userType: filters.userType,
            query: filters.searchTerm,
            status: filters.status,
          });

          try {
            const freshData = await userFetcher(targetUrl);
            const freshUsers = extractUsersFromApiData(freshData);
            globalMutate(targetUrl, freshData, false);
            setAccumulatedUsers((prev) => {
              const updated = [...prev];
              updated.splice(
                targetPage * pageSize,
                freshUsers.length,
                ...freshUsers
              );
              return updated;
            });
          } finally {
            setRefreshLoading(false);
          }
          return;
        }
      }

      const filters = currentFiltersRef.current;
      const firstPageUrl = buildGetUsersUrl({
        pageNo: 0,
        pageSize,
        userType: filters.userType,
        query: filters.searchTerm,
        status: filters.status,
      });

      setPage(0);
      setAccumulatedUsers([]);
      setLoadMoreLoading(false);
      setPreviousPage(0);
      setScrollPosition(0);

      try {
        const freshData = await userFetcher(firstPageUrl);
        const freshUsers = extractUsersFromApiData(freshData);
        globalMutate(firstPageUrl, freshData, false);
        setAccumulatedUsers(freshUsers);
        if (typeof freshData?.count === "number") {
          setTotalCount(freshData.count);
        }
      } finally {
        setRefreshLoading(false);
      }
    };

    refreshUserList();
  }, [refreshCount, editedUserId, mutate]);

  useEffect(() => {
    if (apiData) {
      if (typeof apiData?.count === "number") {
        setTotalCount(apiData.count);
      }

      const newUsers = extractUsersFromApiData(apiData);

      if (page === 0) {
        setAccumulatedUsers(newUsers);
      } else {
        setAccumulatedUsers((prev) => {
          const existingIds = new Set(prev.map((user) => user.id));
          const uniqueNewUsers = newUsers.filter(
            (user) => !existingIds.has(user.id)
          );
          return [...prev, ...uniqueNewUsers];
        });

        setTimeout(() => {
          if (scrollPosition > 0) {
            window.scrollTo(0, scrollPosition);
          }
        }, 100);
      }

      setLoadMoreLoading(false);
    }
  }, [apiData, page, scrollPosition]);

  const apiUserList = accumulatedUsers;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const initialLoading = !apiData && !error && accumulatedUsers.length === 0;

  const showError = error && accumulatedUsers.length === 0;

  const userTableData = useMemo(
    () =>
      apiUserList.map((user) => ({
        id: user.id,
        name: user.name || user.username || "-",
        id_number: user.employee_id || "-",
        phone_number: user.contact_number || "-",
        tagging: getTaggingText([
          ...(user.cluster?.map((item) => item.name) || []),
          ...(user.region?.map((item) => item.name) || []),
          ...(user.area?.map((item) => item.name) || []),
          ...(user.territory?.map((item) => item.name) || []),
          ...(user.agency?.map((item) => item.name) || []),
        ]),
        joining_date: formatDateToReadable(user.joining_date) || "-",
        is_active: Boolean(user.is_active),
      })),
    [apiUserList]
  );

  const renderTableContent = () => {
    if (initialLoading) {
      return (
        <SkeletonTable
          columnCount={8}
          rowCount={7}
          headers={[
            "SL",
            "Manager Name",
            "ID Number",
            "Phone Number",
            "Tagging",
            "Joining Date",
            "Status",
            "",
          ]}
        />
      );
    }

    if (showError) {
      return (
        <ErrorCard
          onRetryClick={mutate}
          title="Error loading users"
          message="Failed to load user list data. Please try again."
        />
      );
    }

    if (userTableData.length === 0 && !initialLoading && !refreshLoading) {
      return (
        <EmptyCard
          title="No users found"
          message={
            searchTerm || status !== "" || userType
              ? "No users found matching your selected filters."
              : "No user data available."
          }
        />
      );
    }

    return (
      <table className="user-table">
        <thead>
          <tr>
            <th>SL</th>
            <th>Manager Name</th>
            <th>ID Number</th>
            <th>Phone Number</th>
            <th>Tagging</th>
            <th>Joining Date</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {userTableData.map((user, index) => (
            <UserRow
              key={user.id || index}
              user={user}
              index={index}
              activeActionMenuId={activeActionMenuId}
              activeActionMenuAnchor={activeActionMenuAnchor}
              onActionMenuToggle={handleActionMenuToggle}
              onCloseActionMenu={closeActionMenu}
              onEditUser={onEditUser}
              onStatusActionClick={onStatusActionClick}
            />
          ))}
          {accumulatedUsers.length < totalCount &&
            !initialLoading &&
            !showError && (
              <tr className="load-more-controls-row">
                <td colSpan="8" className="load-more-controls-cell">
                  <LoadMorePagination
                    page={page}
                    setPage={setPage}
                    totalPages={totalPages}
                    isLoadingMore={loadMoreLoading}
                  />
                </td>
              </tr>
            )}
        </tbody>
      </table>
    );
  };

  return (
    <div className="user-content-container">
      <div className="user-table-container">{renderTableContent()}</div>
      {accumulatedUsers.length > 0 && !initialLoading && !showError && (
        <ResultsCounter
          page={page}
          totalCount={totalCount}
          pageSize={pageSize}
          currentCount={accumulatedUsers.length}
        />
      )}
    </div>
  );
};

export default memo(UserListTable);
